import type { IScope } from "angular";
import { includes, isEqual } from "lodash";
import { finalize } from "rxjs/operators";

import type { ICoverageCalculationsService } from "App/Modules/Gui";
import type {
    IMap,
    IPoint,
    IResponseWithLoading,
    ISite,
    ISiteEquipmentWithCoverages,
    IVideoStream,
    IViewCoverage,
} from "App/Modules/Models";
import { EquipmentClassFunctions } from "App/Modules/Models";

import { EquipmentClass } from "App/Modules/Models/Models/EquipmentClass";
import type { ISiteEquipmentRepository } from "App/Modules/Repositories";
import type { IModalService } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";
import type IMasonryService from "../../../../Services/Masonry/IMasonryService";

export default class SelectedEquipmentComponentController {
    public static $inject: string[] = [
        "$scope",
        "mmSiteEquipmentRepository",
        "mmModalService",
        "mmCoverageCalculationsService",
        "irMasonryService",
    ];

    public selectedSiteEquipment: ISiteEquipmentWithCoverages;

    public siteEquipment: ISiteEquipmentWithCoverages[];

    public map: IMap;

    public site: ISite;

    public onAddToMap: any;

    public onDelete: any;

    public isEditable: boolean;

    public isSidebarCollapsed: boolean;

    public addResponse: IResponseWithLoading<IViewCoverage>;

    public addVideoStreamResponse: IResponseWithLoading<IVideoStream>;

    public patchResponse: IResponseWithLoading<ISiteEquipmentWithCoverages>;

    private sidebarCollapsedWatcher: any;

    constructor(
        private readonly $scope: IScope,
        private readonly siteEquipmentRepository: ISiteEquipmentRepository,
        private readonly modalService: IModalService,
        private readonly coverageCalculationsService: ICoverageCalculationsService,
        private readonly masonryService: IMasonryService,
    ) {}

    public $onInit() {
        this.sidebarCollapsedWatcher = this.$scope.$watch("$ctrl.isSidebarCollapsed", () => {
            this.masonryService.reload();
        });
    }

    public $onDestroy() {
        this.sidebarCollapsedWatcher();
    }

    public editEquipmentDetails() {
        this.modalService
            .open<
                {
                    selectedSiteEquipment: ISiteEquipmentWithCoverages;
                    siteEquipment: ISiteEquipmentWithCoverages[];
                },
                string
            >(
                "mmEditEquipmentDetails",
                {
                    selectedSiteEquipment: this.selectedSiteEquipment,
                    siteEquipment: this.siteEquipment,
                },
                this.site.id,
            )
            .subscribe(
                (response: any) => {
                    if (!response) {
                        this.onDelete({
                            siteEquipment: this.selectedSiteEquipment,
                        });
                    } else {
                        if (response.videoStreams && response.videoStreams.length > 0) {
                            const videoStream = response.videoStreams[0];
                            this.attachVideoStreamToFunctionalities(this.selectedSiteEquipment, videoStream);
                        } else {
                            this.detachVideoStreamFromFunctionalities(this.selectedSiteEquipment);
                        }

                        if (!isEqual(this.selectedSiteEquipment.name, response.name)) {
                            this.selectedSiteEquipment.name = response.name;
                        }

                        if (!isEqual(this.selectedSiteEquipment.type, response.type)) {
                            this.selectedSiteEquipment.type = response.type;
                        }

                        if (!isEqual(this.selectedSiteEquipment.videoStreams, response.videoStreams)) {
                            this.selectedSiteEquipment.videoStreams = response.videoStreams;
                        }

                        if (
                            !isEqual(this.selectedSiteEquipment.externalIntegrationId, response.externalIntegrationId)
                        ) {
                            this.selectedSiteEquipment.externalIntegrationId = response.externalIntegrationId;
                        }

                        if (response.equipment == null) {
                            this.selectedSiteEquipment.equipment = null;
                        }

                        if (response.masterSiteEquipment == null) {
                            this.selectedSiteEquipment.masterSiteEquipment = null;
                            this.detachMasterVideoStreamFromFunctionalities(this.selectedSiteEquipment);
                        } else {
                            this.selectedSiteEquipment.masterSiteEquipment = response.masterSiteEquipment;
                        }
                    }
                },
                (reason) => reason,
            );
    }

    public addViewCoverage() {
        const cameraCoveragePolygon: IPoint[] = [
            {
                x: 0,
                y: 0,
            },
            {
                x: 1,
                y: 0,
            },
            {
                x: 1,
                y: 1,
            },
            {
                x: 0,
                y: 1,
            },
        ];

        const newViewCoverage = {
            aspectRatio: 4 / 3,
            cameraCoveragePolygon,
            downwardTilt: 15,
            horizontalFieldOfView: 60,
            rotationOnMap: this.selectedSiteEquipment.rotationOnMap,
            siteEquipment: this.selectedSiteEquipment,
        } as IViewCoverage;

        this.coverageCalculationsService.calculateMapCoverage(this.map, newViewCoverage);
        newViewCoverage.siteEquipment = null;

        this.coverageCalculationsService.polygonToNormalized(newViewCoverage.mapCoveragePolygon, this.map.image);

        extendWithLoadingAndFail(
            this.siteEquipmentRepository.addViewCoverage(this.site.id, this.selectedSiteEquipment.id, newViewCoverage),
        )
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response: IResponseWithLoading<IViewCoverage>) => {
                    this.addResponse = response;
                    if (!response.hasError && response.data) {
                        response.data.siteEquipment = this.selectedSiteEquipment;
                        this.coverageCalculationsService.polygonToAbsolute(
                            response.data.mapCoveragePolygon,
                            this.map.image,
                        );
                        response.data.functionalities = [];
                        this.selectedSiteEquipment.viewCoverages.push(response.data);
                    }
                },
                (error) => {
                    this.addResponse = error;
                },
            );
    }

    public addVideoStream() {
        extendWithLoadingAndFail(
            this.siteEquipmentRepository.addVideoStream(this.site.id, this.selectedSiteEquipment.id),
        )
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.addVideoStreamResponse = response;

                    if (response.data) {
                        this.selectedSiteEquipment.videoStreams.push(response.data);
                    }
                },
                (error) => {
                    this.addVideoStreamResponse = error;
                },
            );
    }

    public addEquipmentToMap() {
        this.onAddToMap({ siteEquipment: this.selectedSiteEquipment });
    }

    public connectToHardware() {
        this.modalService
            .open<ISiteEquipmentWithCoverages, ISiteEquipmentWithCoverages, ISiteEquipmentWithCoverages>(
                "mmConnectSiteEquipmentToHardware",
                null,
                this.selectedSiteEquipment,
            )
            .subscribe(
                (connectResponse: ISiteEquipmentWithCoverages) => {
                    Object.assign(this.selectedSiteEquipment, {
                        equipment: connectResponse.equipment,
                        type: connectResponse.type,
                    });

                    if (connectResponse.videoStreams && connectResponse.videoStreams.length > 0) {
                        const videoStream = connectResponse.videoStreams[0];

                        this.selectedSiteEquipment.videoStreams = connectResponse.videoStreams;
                        this.attachVideoStreamToFunctionalities(this.selectedSiteEquipment, videoStream);
                    }
                },
                (reason) => reason,
            );
    }

    public showAddCoverage(equipmentClass: EquipmentClass) {
        return EquipmentClassFunctions.showCoveragesForClass(equipmentClass);
    }

    public showAddVideoStream(equipmentClass: EquipmentClass) {
        return (
            equipmentClass === EquipmentClass.DigitalVideoRecorder ||
            equipmentClass === EquipmentClass.NetworkVideoRecorder ||
            equipmentClass === EquipmentClass.Camera
        );
    }

    private attachVideoStreamToFunctionalities(siteEquipment: ISiteEquipmentWithCoverages, videoStream: IVideoStream) {
        siteEquipment.viewCoverages.forEach((vc) => {
            vc.functionalities.forEach((f) => {
                if (f.videoStream === null) {
                    f.videoStream = videoStream;
                }
            });
        });
    }

    private detachVideoStreamFromFunctionalities(siteEquipment: ISiteEquipmentWithCoverages) {
        siteEquipment.viewCoverages.forEach((vc) => {
            vc.functionalities.forEach((f) => {
                if (f.videoStream !== null) {
                    f.videoStream = null;
                }
            });
        });
    }

    private detachMasterVideoStreamFromFunctionalities(siteEquipment: ISiteEquipmentWithCoverages) {
        siteEquipment.viewCoverages.forEach((vc) => {
            vc.functionalities.forEach((f) => {
                if (f.videoStream !== null && !includes(siteEquipment.videoStreams, f.videoStream)) {
                    f.videoStream = null;
                }
            });
        });
    }
}
